import { useCallback, useEffect, useRef, useState } from 'react';

import clsx from 'clsx';

import { h2 } from '@frontend/storefront/components/home/styles';
import Image from '@frontend/storefront/Image';

type Gift = { label: string; src: string };

// Horizontal scroll-snap carousel with prev/next arrows.
export function GiftCarousel({
  heading,
  gifts,
}: {
  heading: string;
  gifts: Gift[];
}) {
  const trackRef = useRef<HTMLDivElement>(null);
  const [canPrev, setCanPrev] = useState(false);
  const [canNext, setCanNext] = useState(true);

  const update = useCallback(() => {
    const track = trackRef.current;
    if (!track) return;
    const { scrollLeft, scrollWidth, clientWidth } = track;
    setCanPrev(scrollLeft > 1);
    setCanNext(scrollLeft + clientWidth < scrollWidth - 1);
  }, []);

  useEffect(() => {
    const track = trackRef.current;
    if (!track) return;
    const observer = new ResizeObserver(update);
    observer.observe(track);
    return () => observer.disconnect();
  }, [update]);

  const step = (dir: number) => {
    const track = trackRef.current;
    const slide = track?.firstElementChild as HTMLElement | null;
    if (!track || !slide) return;
    const gap = parseFloat(getComputedStyle(track).columnGap) || 0;
    track.scrollBy({ left: dir * (slide.offsetWidth + gap) });
  };

  return (
    <section className="np-section">
      <div className="np-container">
        <div className="mb-6 flex items-center justify-between gap-4">
          <h2 className={clsx(h2, 'mb-0')}>{heading}</h2>
          <div className="hidden gap-2 tab:flex">
            <ArrowButton
              label="Previous"
              disabled={!canPrev}
              onClick={() => step(-1)}
              path="M12.5 4 6.5 10l6 6"
            />
            <ArrowButton
              label="Next"
              disabled={!canNext}
              onClick={() => step(1)}
              path="m7.5 4 6 6-6 6"
            />
          </div>
        </div>

        <div
          ref={trackRef}
          onScroll={update}
          tabIndex={0}
          aria-label={heading}
          className="grid snap-x snap-mandatory auto-cols-[62%] grid-flow-col gap-2 overflow-x-auto overscroll-x-contain scroll-smooth [scrollbar-width:none] tab:auto-cols-[calc((100%-8px)/2.3)] desk:auto-cols-[calc((100%-24px)/4)] [&::-webkit-scrollbar]:hidden"
        >
          {gifts.map((gift) => (
            <div
              key={gift.label}
              className="relative aspect-square snap-start overflow-hidden"
            >
              <Image
                src={gift.src}
                alt={gift.label}
                fill
                sizes="(min-width: 990px) 25vw, 60vw"
                className="object-cover"
              />
              <span className="absolute bottom-[10%] left-1/2 -translate-x-1/2 rounded-lg bg-np-tag px-2.5 py-1.5 font-hand text-lg leading-none whitespace-nowrap tab:px-3 tab:py-2 tab:text-2xl">
                {gift.label}
              </span>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function ArrowButton({
  label,
  disabled,
  onClick,
  path,
}: {
  label: string;
  disabled: boolean;
  onClick: () => void;
  path: string;
}) {
  return (
    <button
      type="button"
      aria-label={label}
      disabled={disabled}
      onClick={onClick}
      className="inline-flex size-11 cursor-pointer items-center justify-center rounded-full border-[1.5px] border-np-ink text-np-ink transition enabled:hover:bg-np-ink enabled:hover:text-np-bg disabled:cursor-default disabled:opacity-30"
    >
      <svg width="20" height="20" viewBox="0 0 20 20" aria-hidden="true">
        <path
          d={path}
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    </button>
  );
}
